// Holdings en cobro y en Empresas: funciones puras compartidas por el asistente de cobro, la
// lista y el detalle de notas, y la ficha del holding.
//
// Un holding se cobra con UNA nota consolidada por período y absorbe a sus empresas: una
// empresa de un holding del operador no se cobra sola. La membresía (regla de la unión de
// `holding.companies[]` y `company.holdingId`) la resuelve microservice-contracts; acá solo se
// lee lo que devuelve `GET contracts/holdings`.

import { buildCommerceSearchKey } from '../../../../utils/commerceSearch';
import { apiErrorCode, apiErrorMessage, apiErrorStatus } from './collectionReminders';

/** Tipos de unidad que cobra el asistente: un comercio suelto o un holding. */
export const UNIT_COMPANY = 'company';
export const UNIT_HOLDING = 'holding';

/**
 * Clave de una unidad de cobro para la selección, los ajustes y los mapas del asistente. Un
 * comercio y un holding nunca comparten clave aunque compartieran id.
 *
 * @param {{ kind: string, value: string }} unit
 */
export const unitKey = (unit) => `${unit.kind}:${unit.value}`;

// Id como string, tolerando un subdocumento poblado ({ _id }) o un ObjectId serializado.
const idOf = (value) =>
  String((typeof value === 'object' && value !== null ? value._id ?? value.id : value) ?? '');

/**
 * Opción de selector de un holding de `GET contracts/holdings`, con la misma forma plana que
 * `toCommerceOption` (name, legalName, businessIdentifier, searchKey) para que el buscador por
 * nombre, razón social o RUT funcione igual sobre comercios y holdings.
 */
export function toHoldingOption(holding, fallbackLabel = '') {
  const name = holding?.name || '';
  const legalName = holding?.legalName ?? null;
  const businessIdentifier = holding?.businessIdentifier ?? null;
  return {
    kind: UNIT_HOLDING,
    value: idOf(holding?.id),
    label: name || fallbackLabel,
    name,
    legalName,
    businessIdentifier,
    memberCount: Array.isArray(holding?.companies) ? holding.companies.length : 0,
    searchKey: buildCommerceSearchKey({ name, legalName, businessIdentifier }),
  };
}

/**
 * Ids de todas las empresas que pertenecen a algún holding del operador. El asistente no las
 * ofrece sueltas: billing las omitiría (`belongs_to_holding`) porque las cobra su holding.
 *
 * @param {Array<{ companies?: Array<{ id: string }> }>} holdings
 * @returns {Set<string>}
 */
export function holdingMemberIds(holdings) {
  const ids = new Set();
  (Array.isArray(holdings) ? holdings : []).forEach((h) =>
    (Array.isArray(h?.companies) ? h.companies : []).forEach((c) => {
      const id = idOf(c?.id);
      if (id) ids.add(id);
    }),
  );
  return ids;
}

/**
 * Holdings pedidos a billing que su respuesta no menciona en ninguno de los arreglos dados
 * (p. ej. `perHolding` y `skipped` de la vista previa, o `created` y `skipped` de generate).
 *
 * Un billing anterior a los holdings ignora `holdingIds` sin error y responde solo por los
 * comercios: sin este control el asistente seguiría como si nada, y las empresas de esos
 * holdings, que no se ofrecen sueltas, no se cobrarían por ninguna vía.
 *
 * @param {string[]|undefined} requested
 * @param {...Array<{ holdingId?: unknown }>|undefined} lists
 * @returns {string[]}
 */
export function unansweredHoldingIds(requested, ...lists) {
  const answered = new Set();
  lists.forEach((list) =>
    (Array.isArray(list) ? list : []).forEach((item) => {
      const id = idOf(item?.holdingId);
      if (id) answered.add(id);
    }),
  );
  return (Array.isArray(requested) ? requested : []).map(String).filter((id) => !answered.has(id));
}

/**
 * Holdings pedidos que la vista previa no trae: todos si falta `perHolding` (billing sin
 * holdings), si no los que no están ni en `perHolding` ni en `skipped`.
 */
export const holdingsMissingFromPreview = (requested, preview) =>
  Array.isArray(preview?.perHolding)
    ? unansweredHoldingIds(requested, preview.perHolding, preview.skipped)
    : (Array.isArray(requested) ? requested : []).map(String);

/** Id del holding de una nota consolidada, o '' si es la nota de un comercio. */
export const noteHoldingId = (note) => idOf(note?.holdingId);

/** ¿Es la nota consolidada de un holding? */
export const isHoldingNote = (note) => noteHoldingId(note) !== '';

/** Ruta de la ficha del holding en Empresas (pestaña Holdings). */
export const holdingFichaPath = (holdingId) =>
  `/admin/contracts?tab=holdings&holding=${encodeURIComponent(holdingId)}`;

/**
 * Rótulo (clave i18n) de cada dato que puede faltar para emitir la nota del holding. Los
 * fiscales salen de contracts (`missingFiscalFields`) y los de contacto y plazo del perfil de
 * cobro de billing; la vista previa los combina en `missingProfileFields`.
 */
export const HOLDING_MISSING_FIELD_LABELS = {
  legalName: 'razón social',
  businessIdentifier: 'RUT',
  activity: 'giro',
  'fiscalAddress.street': 'calle y número de la dirección fiscal',
  'fiscalAddress.municipality': 'comuna de la dirección fiscal',
  'fiscalAddress.state': 'región de la dirección fiscal',
  contactName: 'nombre del contacto de facturación',
  contactEmails: 'correo de facturación',
  contactPhone: 'teléfono de facturación',
  paymentTerm: 'plazo de pago',
  // Solo en el 409 holding_not_ready del interruptor "Cobro consolidado".
  pricing: 'tarifa propia vigente',
  missing_pricing: 'tarifa propia vigente',
  missingPricing: 'tarifa propia vigente',
};

// La tarifa en la lista `missing` del 409 holding_not_ready. billing la informa como
// `missingPricing` en la vista previa; se aceptan las variantes del código.
const PRICING_MISSING_CODES = ['pricing', 'missing_pricing', 'missingPricing'];

/**
 * Sección de la ficha del holding donde se completa cada dato faltante: 'fiscal' (contracts),
 * 'contacto' (perfil de cobro de billing) o 'tarifa' (billing). null si el código no se conoce.
 */
export function holdingFieldSection(field) {
  if (PRICING_MISSING_CODES.includes(field)) return 'tarifa';
  if (['contactName', 'contactEmails', 'contactPhone', 'paymentTerm'].includes(field)) {
    return 'contacto';
  }
  if (
    ['legalName', 'businessIdentifier', 'activity'].includes(field) ||
    String(field).startsWith('fiscalAddress.')
  ) {
    return 'fiscal';
  }
  return null;
}

/**
 * ¿Tiene encendido el "Cobro consolidado"? Acepta la vista del perfil de billing (GET/PUT por
 * holding, con el campo arriba o dentro de `profile`), un ítem del listado o un holding ya
 * combinado. Lo que no dice `true` está apagado: es el default del perfil.
 */
export const consolidatedEnabledOf = (view) =>
  (view?.consolidatedEnabled ?? view?.profile?.consolidatedEnabled) === true;

/** Desde cuándo está encendido (ISO) o null. */
export const consolidatedEnabledAtOf = (view) =>
  view?.consolidatedEnabledAt ?? view?.profile?.consolidatedEnabledAt ?? null;

/**
 * Holdings de contracts con el estado del cobro consolidado de billing
 * (`GET billing/collections/holding-profiles`): cada uno gana `consolidatedEnabled` (boolean) y
 * `consolidatedEnabledAt`. Un holding sin ítem en el listado está apagado.
 *
 * Solo los ENCENDIDOS absorben a sus empresas y se cobran con nota consolidada; las empresas
 * de un holding apagado se cobran sueltas.
 */
export function withConsolidation(holdings, profileItems) {
  const byHolding = new Map(
    (Array.isArray(profileItems) ? profileItems : []).map((item) => [idOf(item?.holdingId), item]),
  );
  return (Array.isArray(holdings) ? holdings : []).map((h) => {
    const item = byHolding.get(idOf(h?.id));
    return {
      ...h,
      consolidatedEnabled: consolidatedEnabledOf(item),
      consolidatedEnabledAt: consolidatedEnabledAtOf(item),
    };
  });
}

/** Los holdings con el cobro consolidado encendido. */
export const consolidatedHoldings = (holdings) =>
  (Array.isArray(holdings) ? holdings : []).filter((h) => h?.consolidatedEnabled === true);

/** Datos fiscales que contracts no deja editar por API: los carga soporte. */
export const NON_EDITABLE_FISCAL_FIELDS = ['legalName', 'businessIdentifier'];

/** "giro, correo de facturación" — los datos faltantes en texto, en el orden recibido. */
export const missingFieldsText = (fields, t) =>
  (Array.isArray(fields) ? fields : [])
    .map((field) => t(HOLDING_MISSING_FIELD_LABELS[field] ?? field))
    .join(', ');

/** Rótulo (clave i18n) de por qué una empresa figura en el holding. */
export const MEMBERSHIP_SOURCE_LABELS = {
  both: 'Vinculada',
  list: 'Solo en la lista del holding',
  holdingId: 'Solo en la ficha de la empresa',
};

/**
 * Motivo (clave i18n) por el que billing no emite la nota de un holding: `skipped[].reason` de
 * generate (y de preview) y los `blockers` de la vista previa.
 */
export const HOLDING_SKIP_REASON_LABELS = {
  consolidated_disabled: 'cobro consolidado apagado',
  missing_pricing: 'sin tarifa propia',
  missing_profile_fields: 'faltan datos del holding',
  membership_conflict: 'empresas en más de un holding',
  cycle_mismatch: 'el período no es su ciclo de facturación',
  no_packages: 'sin paquetes en el período',
  no_priced_packages: 'sin paquetes tarifados',
  billed_concurrently: 'sus paquetes se cobraron en paralelo',
  pricing_changed: 'la tarifa cambió desde la vista previa',
  volume_changed: 'el volumen cambió desde la vista previa',
};

/** Texto del motivo; un motivo desconocido se muestra tal cual (mejor que nada). */
export const holdingSkipReasonText = (reason, t) =>
  HOLDING_SKIP_REASON_LABELS[reason] ? t(HOLDING_SKIP_REASON_LABELS[reason]) : String(reason ?? '');

// Textos (claves i18n) de los códigos de error de contracts y de billing sobre holdings.
const HOLDING_ERROR_LABELS = {
  holding_not_found: 'El holding no existe o no es de tu operador.',
  invalid_holding_id: 'El identificador del holding no es válido.',
  company_not_found: 'La empresa no existe o no tiene un contrato activo con tu operador.',
  company_in_other_holding: 'Esa empresa ya pertenece a otro holding de tu operador.',
  company_multi_operator:
    'Esa empresa también tiene un contrato activo con otro operador: no se puede vincular a un holding.',
  membership_changed: 'La membresía del holding cambió mientras se guardaba. Volvé a intentarlo.',
  holdings_unavailable:
    'No se pudieron consultar los holdings en este momento, así que no se puede calcular ni emitir el cobro. Reintentá en unos minutos.',
  pricing_version_conflict:
    'Otra sesión guardó la tarifa del holding antes que vos: recargá para ver la versión vigente y volvé a aplicar tus cambios.',
  holding_profile_conflict:
    'El perfil de cobro del holding se guardó desde otra sesión. Recargá y volvé a guardar.',
  forbidden_role: 'Tu rol no tiene permiso para gestionar los holdings.',
  operator_required: 'Tu usuario no pertenece a un operador: no puede gestionar holdings.',
};

/** Texto del código de error de holdings de la respuesta, o null si no trae uno conocido. */
export function holdingCodeText(err, t) {
  const code = apiErrorCode(err);
  return code && HOLDING_ERROR_LABELS[code] ? t(HOLDING_ERROR_LABELS[code]) : null;
}

/**
 * Texto de un error de las APIs de holdings (contracts o billing): el del código conocido, el
 * de un 403 genérico, o el mensaje del servidor; si no hay nada, `fallback` (clave i18n).
 */
export function holdingErrorText(err, t, fallback) {
  const known = holdingCodeText(err, t);
  if (known) return known;
  if (apiErrorStatus(err) === 403) return t('Tu rol no tiene permiso para gestionar los holdings.');
  return apiErrorMessage(err) ?? t(fallback);
}
